// ============================================================================
// INPUT MANAGER - Keyboard, Mouse, Gamepad
// ============================================================================

import { Vector2 } from '@/shared/types';

export interface InputState {
  // Movement
  moveX: number;
  moveY: number;
  
  // Aiming (world position)
  aimX: number;
  aimY: number;
  
  // Actions
  shoot: boolean;
  reload: boolean;
  interact: boolean;
  throwGrenade: boolean;
  useBombCall: boolean;
  useMissileStrike: boolean;
  ability1: boolean;
  ability2: boolean;
  ultimate: boolean;
  
  // Weapon switching (1-5)
  weaponSlot: number;  // 0 = no switch, 1-10 = weapon slot
  
  // UI
  menu: boolean;
  scoreboard: boolean;
  buyMenu: boolean;
}

export type KeyBinding = {
  action: keyof InputState;
  key: string;
  type: 'press' | 'hold';
};

const DEFAULT_BINDINGS: KeyBinding[] = [
  { action: 'reload', key: 'KeyR', type: 'press' },
  { action: 'interact', key: 'KeyE', type: 'hold' },  // Hold for planting/defusing spike
  { action: 'throwGrenade', key: 'KeyG', type: 'press' },
  { action: 'useBombCall', key: 'KeyV', type: 'press' },
  { action: 'useMissileStrike', key: 'KeyJ', type: 'press' },
  { action: 'ability1', key: 'KeyQ', type: 'press' },
  { action: 'ability2', key: 'KeyF', type: 'press' },
  { action: 'ultimate', key: 'KeyX', type: 'press' },
  { action: 'menu', key: 'Escape', type: 'press' },
  { action: 'scoreboard', key: 'Tab', type: 'hold' },
  { action: 'buyMenu', key: 'KeyB', type: 'press' },
];

export class InputManager {
  private canvas: HTMLCanvasElement;
  
  // Raw input state
  private keysDown = new Set<string>();
  private keysPressed = new Set<string>();
  private mouseDown = new Set<number>();
  private mousePressed = new Set<number>();
  private mousePosition: Vector2 = { x: 0, y: 0 };
  private mouseWorldPosition: Vector2 = { x: 0, y: 0 };
  private mouseWheelDelta = 0; // Positive = scroll down, Negative = scroll up
  
  // Processed state
  private currentState: InputState;
  private previousState: InputState;
  
  // Camera reference for screen-to-world conversion
  private cameraOffset: Vector2 = { x: 0, y: 0 };
  private cameraZoom = 1;
  
  // Bindings
  private bindings: KeyBinding[];
  
  // Sequence number for network ordering
  private sequence = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.bindings = [...DEFAULT_BINDINGS];
    this.currentState = this.createEmptyState();
    this.previousState = this.createEmptyState();
    
    this.setupListeners();
  }

  private createEmptyState(): InputState {
    return {
      moveX: 0,
      moveY: 0,
      aimX: 0,
      aimY: 0,
      shoot: false,
      reload: false,
      interact: false,
      throwGrenade: false,
      useBombCall: false,
      useMissileStrike: false,
      ability1: false,
      ability2: false,
      ultimate: false,
      weaponSlot: 0,
      menu: false,
      scoreboard: false,
      buyMenu: false,
    };
  }

  private setupListeners(): void {
    // Keyboard
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    
    // Mouse
    this.canvas.addEventListener('mousedown', this.onMouseDown);
    this.canvas.addEventListener('mouseup', this.onMouseUp);
    this.canvas.addEventListener('mousemove', this.onMouseMove);
    this.canvas.addEventListener('wheel', this.onMouseWheel, { passive: false });
    this.canvas.addEventListener('contextmenu', (e) => e.preventDefault());
    
    // Prevent tab from changing focus
    window.addEventListener('keydown', (e) => {
      if (e.code === 'Tab') e.preventDefault();
    });
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    if (!this.keysDown.has(e.code)) {
      this.keysPressed.add(e.code);
    }
    this.keysDown.add(e.code);
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    this.keysDown.delete(e.code);
  };

  private onMouseDown = (e: MouseEvent): void => {
    if (!this.mouseDown.has(e.button)) {
      this.mousePressed.add(e.button);
    }
    this.mouseDown.add(e.button);
  };

  private onMouseUp = (e: MouseEvent): void => {
    this.mouseDown.delete(e.button);
  };

  private onMouseWheel = (e: WheelEvent): void => {
    e.preventDefault(); // Prevent page scroll
    this.mouseWheelDelta = e.deltaY;
  };

  private onMouseMove = (e: MouseEvent): void => {
    const rect = this.canvas.getBoundingClientRect();
    this.mousePosition = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
    
    // Convert to world position
    this.mouseWorldPosition = {
      x: (this.mousePosition.x / this.cameraZoom) + this.cameraOffset.x,
      y: (this.mousePosition.y / this.cameraZoom) + this.cameraOffset.y,
    };
  };

  update(): void {
    // Store previous state
    this.previousState = { ...this.currentState };
    
    // Reset state
    this.currentState = this.createEmptyState();
    
    // Process movement (WASD)
    if (this.keysDown.has('KeyW') || this.keysDown.has('ArrowUp')) {
      this.currentState.moveY -= 1;
    }
    if (this.keysDown.has('KeyS') || this.keysDown.has('ArrowDown')) {
      this.currentState.moveY += 1;
    }
    if (this.keysDown.has('KeyA') || this.keysDown.has('ArrowLeft')) {
      this.currentState.moveX -= 1;
    }
    if (this.keysDown.has('KeyD') || this.keysDown.has('ArrowRight')) {
      this.currentState.moveX += 1;
    }
    
    // Normalize diagonal movement
    const moveLen = Math.sqrt(
      this.currentState.moveX * this.currentState.moveX +
      this.currentState.moveY * this.currentState.moveY
    );
    if (moveLen > 1) {
      this.currentState.moveX /= moveLen;
      this.currentState.moveY /= moveLen;
    }
    
    // Process aim
    this.currentState.aimX = this.mouseWorldPosition.x;
    this.currentState.aimY = this.mouseWorldPosition.y;
    
    // Process shoot (left mouse button)
    this.currentState.shoot = this.mouseDown.has(0);
    
    // Process reload (R key OR right mouse button)
    if (this.mousePressed.has(2)) {
      this.currentState.reload = true;
    }
    
    // Process key bindings
    for (const binding of this.bindings) {
      if (binding.type === 'press') {
        if (this.keysPressed.has(binding.key)) {
          (this.currentState as unknown as Record<string, boolean>)[binding.action] = true;
        }
      } else {
        if (this.keysDown.has(binding.key)) {
          (this.currentState as unknown as Record<string, boolean>)[binding.action] = true;
        }
      }
    }
    
    // Process weapon switching (number keys 1-9, 0 for slot 10, OR mouse wheel)
    if (this.keysPressed.has('Digit1')) this.currentState.weaponSlot = 1;
    else if (this.keysPressed.has('Digit2')) this.currentState.weaponSlot = 2;
    else if (this.keysPressed.has('Digit3')) this.currentState.weaponSlot = 3;
    else if (this.keysPressed.has('Digit4')) this.currentState.weaponSlot = 4;
    else if (this.keysPressed.has('Digit5')) this.currentState.weaponSlot = 5;
    else if (this.keysPressed.has('Digit6')) this.currentState.weaponSlot = 6;
    else if (this.keysPressed.has('Digit7')) this.currentState.weaponSlot = 7;
    else if (this.keysPressed.has('Digit8')) this.currentState.weaponSlot = 8;
    else if (this.keysPressed.has('Digit9')) this.currentState.weaponSlot = 9;
    else if (this.keysPressed.has('Digit0')) this.currentState.weaponSlot = 10;
    else if (this.mouseWheelDelta !== 0) {
      // Mouse wheel weapon cycling
      // deltaY > 0 = scroll down = next weapon, deltaY < 0 = scroll up = previous weapon
      this.currentState.weaponSlot = this.mouseWheelDelta > 0 ? -1 : -2; // -1 = next, -2 = previous
      this.mouseWheelDelta = 0; // Reset after processing
    }
    
    // Clear pressed states (they only last one frame)
    this.keysPressed.clear();
    this.mousePressed.clear();
  }

  getState(): InputState {
    return { ...this.currentState };
  }

  wasPressed(action: keyof InputState): boolean {
    return this.currentState[action] === true && this.previousState[action] === false;
  }

  wasReleased(action: keyof InputState): boolean {
    return this.currentState[action] === false && this.previousState[action] === true;
  }

  isDown(action: keyof InputState): boolean {
    return this.currentState[action] === true;
  }

  getMousePosition(): Vector2 {
    return { ...this.mousePosition };
  }

  getMouseWorldPosition(): Vector2 {
    return { ...this.mouseWorldPosition };
  }

  updateCamera(offset: Vector2, zoom: number): void {
    this.cameraOffset = offset;
    this.cameraZoom = zoom;
    
    // Recalculate world position
    this.mouseWorldPosition = {
      x: (this.mousePosition.x / this.cameraZoom) + this.cameraOffset.x,
      y: (this.mousePosition.y / this.cameraZoom) + this.cameraOffset.y,
    };
  }

  getNextSequence(): number {
    return ++this.sequence;
  }

  setBindings(bindings: KeyBinding[]): void {
    this.bindings = bindings;
  }

  /**
   * Reset all input state - call this when transitioning between rounds
   * to prevent held inputs from carrying over
   */
  resetAllInputs(): void {
    this.keysDown.clear();
    this.keysPressed.clear();
    this.mouseDown.clear();
    this.mousePressed.clear();
    this.mouseWheelDelta = 0;
    this.currentState = this.createEmptyState();
    this.previousState = this.createEmptyState();
  }

  destroy(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    this.canvas.removeEventListener('mousedown', this.onMouseDown);
    this.canvas.removeEventListener('mouseup', this.onMouseUp);
    this.canvas.removeEventListener('mousemove', this.onMouseMove);
  }
}
